/* eslint-disable react/no-unescaped-entities */
/**
 * Trial Funnel — meerstaps wizard, AI-gegenereerd plan, vergrendelde preview.
 * Nederlandstalig. Premium dark aurora theme.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Lock, Sparkles, Check, X, Mail, Instagram } from 'lucide-react';
import funnelApi, { funnelStorage } from '../services/funnelApi';
import '../styles/Funnel.css';

const FITNESS_FACTS = [
  { c: 'Nutrition', t: 'Protein needs spike post-workout — aim for 0.3g per kg bodyweight within 2 hours of training.' },
  { c: 'Training', t: 'You build more muscle from leaving 1-3 reps in reserve than from grinding to failure every set.' },
  { c: 'Recovery', t: 'Sleep < 7 hours can drop your max strength output by up to 20% the next day.' },
  { c: 'Nutrition', t: 'Eating carbs around your workout actually helps fat loss — they fuel intensity without blunting it.' },
  { c: 'Training', t: 'Consistency over 12 weeks beats intensity over 2. Show up, even if it\u2019s a shorter session.' },
  { c: 'Hydration', t: 'A 2% drop in hydration cuts strength performance by ~10%. Sip throughout — don\u2019t chug at the start.' },
  { c: 'Mindset', t: 'The first 4 weeks are 80% neural adaptation, not muscle. You get stronger before you look stronger.' },
  { c: 'Nutrition', t: 'Fiber slows digestion and keeps you full — most people need 30-40g/day, but average closer to 15g.' },
  { c: 'Training', t: 'Compound lifts (squat, deadlift, press) recruit ~3x more muscle than isolation work.' },
  { c: 'Recovery', t: 'Walking 8-10k steps on rest days speeds recovery more than lying still.' },
];

const DEFAULT_ANSWERS = {
  language: 'nl',
  training_type: null,
  training_type_other: '',
  goal: null,
  goal_other: '',
  level: null,
  gender: null,
  gender_other: '',
  age: null,
  height_cm: null,
  weight_kg: null,
  target_weight_kg: null,
  pregnancy: { is_pregnant: false, weeks: null, due_date: null },
  days_per_week: null,
  availability: [
    { day: 'mon', available: false, time_slot: 'evening', minutes: 60 },
    { day: 'tue', available: false, time_slot: 'evening', minutes: 60 },
    { day: 'wed', available: false, time_slot: 'evening', minutes: 60 },
    { day: 'thu', available: false, time_slot: 'evening', minutes: 60 },
    { day: 'fri', available: false, time_slot: 'evening', minutes: 60 },
    { day: 'sat', available: false, time_slot: 'morning', minutes: 60 },
    { day: 'sun', available: false, time_slot: 'morning', minutes: 60 },
  ],
  equipment: [],
  injuries: '',
  diet_type: 'omnivore',
  diet_type_other: '',
  allergies: [],
  dislikes: [],
  plan_duration_weeks: null,
};

const TRAINING_TYPES = [
  { value: 'gym', label: 'Gym', tag: 'strength training' },
  { value: 'running', label: 'Running', tag: 'cardio' },
  { value: 'cycling', label: 'Cycling', tag: 'cardio' },
  { value: 'home', label: 'At home', tag: 'bodyweight / light gear' },
  { value: 'combination', label: 'Combination', tag: 'strength + cardio' },
  { value: 'other', label: 'Other', tag: 'tell us yourself' },
];

const GOALS = [
  { value: 'lose_weight', label: 'Lose weight', tag: 'fat loss' },
  { value: 'build_muscle', label: 'Build muscle', tag: 'more mass' },
  { value: 'improve_cardio', label: 'Improve cardio', tag: 'endurance' },
  { value: 'get_fitter', label: 'Get fitter', tag: 'general fitness' },
  { value: 'recomp', label: 'Body recomp', tag: 'lose fat + build muscle' },
  { value: 'other', label: 'Other', tag: 'tell us yourself' },
];

const LEVELS = [
  { value: 'beginner', label: 'Beginner', tag: '< 6 months experience' },
  { value: 'intermediate', label: 'Intermediate', tag: '6 months - 2 years' },
  { value: 'expert', label: 'Expert', tag: '2+ years experience' },
];

const GENDERS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

const DIET_TYPES = [
  { value: 'omnivore', label: 'No restrictions' },
  { value: 'vegetarian', label: 'Vegetarian' },
  { value: 'vegan', label: 'Vegan' },
  { value: 'pescatarian', label: 'Pescatarian' },
  { value: 'keto', label: 'Keto / low-carb' },
  { value: 'halal', label: 'Halal' },
  { value: 'kosher', label: 'Kosher' },
  { value: 'other', label: 'Other' },
];

const DAY_LABELS = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };

const EQUIPMENT_LABELS = {
  barbell: 'Barbell',
  dumbbells: 'Dumbbells',
  kettlebell: 'Kettlebells',
  resistance_bands: 'Resistance bands',
  pull_up_bar: 'Pull-up bar',
  bench: 'Bench',
  rack: 'Squat rack',
  cable_machine: 'Cable machine',
  leg_press: 'Leg press',
  smith_machine: 'Smith machine',
  rowing_machine: 'Rowing machine',
  treadmill: 'Treadmill',
  stationary_bike: 'Stationary bike',
  elliptical: 'Elliptical',
  outdoor_bike: 'Bike (outdoor)',
  running_shoes: 'Running shoes',
  yoga_mat: 'Yoga mat',
  foam_roller: 'Foam roller',
  trx: 'TRX',
  medicine_ball: 'Medicine ball',
  jump_rope: 'Jump rope',
  swimming_pool: 'Swimming pool',
  none: 'None (bodyweight only)',
};

const PLAN_DURATIONS = [
  { value: 2, label: '2 weeks', tag: 'short intro plan' },
  { value: 4, label: '4 weeks', tag: 'standard cycle' },
  { value: 8, label: '8 weeks', tag: 'mid-term progression' },
  { value: 12, label: '12 weeks', tag: 'full program' },
];

const Funnel = () => {
  const navigate = useNavigate();
  const initial = useMemo(() => {
    const saved = funnelStorage.load();
    if (!saved) return null;
    return saved;
  }, []);
  const [step, setStep] = useState(initial?.step ?? 0);
  const [answers, setAnswers] = useState(initial?.answers ? { ...DEFAULT_ANSWERS, ...initial.answers } : DEFAULT_ANSWERS);
  const [sessionId, setSessionId] = useState(initial?.sessionId ?? null);
  const [equipmentList, setEquipmentList] = useState([]);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [planId, setPlanId] = useState(initial?.planId ?? null);
  const [planPreview, setPlanPreview] = useState(initial?.planPreview ?? null);
  const [loadingMessage, setLoadingMessage] = useState('Analyzing your profile...');

  // Restore-from-storage is handled at useState initial values above.

  // Persist on changes
  useEffect(() => {
    funnelStorage.save({ step, answers, sessionId, planId, planPreview });
  }, [step, answers, sessionId, planId, planPreview]);

  // Boot session + equipment
  useEffect(() => {
    (async () => {
      try {
        if (!sessionId) {
          const sid = await funnelApi.startSession();
          setSessionId(sid);
        }
        if (equipmentList.length === 0) {
          const eq = await funnelApi.getEquipment();
          setEquipmentList(eq);
        }
      } catch {
        /* network — non-fatal until submit */
      }
    })();
  }, [sessionId, equipmentList.length]);

  const update = useCallback((patch) => {
    setAnswers((prev) => ({ ...prev, ...patch }));
    setError(null);
  }, []);

  const steps = useMemo(() => {
    const isFemale = answers.gender === 'female';
    const order = [
      'training_type',
      'goal',
      'level',
      'gender_age',
      'body_metrics',
    ];
    if (isFemale) order.push('pregnancy');
    order.push(
      'days_per_week',
      'availability',
      'equipment',
      'injuries',
      'diet',
      'plan_duration',
    );
    return order;
  }, [answers.gender]);

  const totalSteps = steps.length;
  const currentKey = steps[step];
  const progressPct = Math.round(((step + 1) / totalSteps) * 100);

  const validateCurrent = () => {
    switch (currentKey) {
      case 'training_type':
        if (!answers.training_type) return 'Pick a training type';
        if (answers.training_type === 'other' && !(answers.training_type_other || '').trim()) return 'Describe your training type in the box';
        break;
      case 'goal':
        if (!answers.goal) return 'Pick a goal';
        if (answers.goal === 'other' && !(answers.goal_other || '').trim()) return 'Describe your goal in the box';
        break;
      case 'level':
        if (!answers.level) return 'Pick your level';
        break;
      case 'gender_age':
        if (!answers.gender) return 'Pick a gender';
        if (answers.gender === 'other' && !(answers.gender_other || '').trim()) return 'Tell us how you identify in the box';
        if (!answers.age || answers.age < 14 || answers.age > 90) return 'Enter a valid age (14-90)';
        break;
      case 'body_metrics':
        if (!answers.height_cm || answers.height_cm < 120 || answers.height_cm > 230) return 'Enter a valid height (120-230 cm)';
        if (!answers.weight_kg || answers.weight_kg < 35 || answers.weight_kg > 250) return 'Enter a valid weight (35-250 kg)';
        if (answers.target_weight_kg !== null && answers.target_weight_kg !== '' && (answers.target_weight_kg < 35 || answers.target_weight_kg > 250)) return 'Target weight is invalid';
        break;
      case 'pregnancy':
        if (answers.pregnancy.is_pregnant) {
          if (answers.pregnancy.weeks === null || answers.pregnancy.weeks === '') return 'Enter the number of weeks';
          if (!answers.pregnancy.due_date) return 'Enter your due date';
        }
        break;
      case 'days_per_week':
        if (!answers.days_per_week) return 'Pick how many days per week you want to train';
        break;
      case 'availability': {
        const avail = answers.availability.filter((d) => d.available);
        if (avail.length === 0) return 'Select at least one day';
        if (avail.length < answers.days_per_week) return `You picked ${answers.days_per_week} training days — mark at least that many days available`;
        break;
      }
      case 'equipment':
        if (answers.equipment.length === 0) return 'Pick at least one option (choose "None" if you have no equipment)';
        break;
      case 'diet':
        if (!answers.diet_type) return 'Pick a diet';
        if (answers.diet_type === 'other' && !(answers.diet_type_other || '').trim()) return 'Describe your diet in the box';
        break;
      case 'plan_duration':
        if (!answers.plan_duration_weeks) return 'Pick a plan duration';
        break;
      default:
        break;
    }
    return null;
  };

  const handleNext = async () => {
    const err = validateCurrent();
    if (err) {
      setError(err);
      return;
    }
    if (step < totalSteps - 1) {
      setStep(step + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      await handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
      setError(null);
    }
  };

  const buildPayload = () => {
    const a = { ...answers };
    // Strip empty optional fields, clean numbers
    if (a.target_weight_kg === '' || a.target_weight_kg === null) delete a.target_weight_kg;
    if (a.injuries && !a.injuries.trim()) a.injuries = null;
    if (!a.pregnancy?.is_pregnant) a.pregnancy = { is_pregnant: false };
    // Clean "other" free-text fields — only send when relevant
    ['training_type_other', 'goal_other', 'gender_other', 'diet_type_other'].forEach((k) => {
      if (!a[k] || !a[k].trim()) {
        delete a[k];
      } else {
        a[k] = a[k].trim().slice(0, 120);
      }
    });
    // Coerce numerics
    a.age = Number(a.age);
    a.height_cm = Number(a.height_cm);
    a.weight_kg = Number(a.weight_kg);
    if (a.target_weight_kg !== undefined) a.target_weight_kg = Number(a.target_weight_kg);
    a.days_per_week = Number(a.days_per_week);
    a.plan_duration_weeks = Number(a.plan_duration_weeks);
    a.availability = a.availability.map((d) => ({
      day: d.day,
      available: !!d.available,
      time_slot: d.available ? d.time_slot : null,
      minutes: d.available ? Number(d.minutes) || 60 : null,
    }));
    return a;
  };

  const handleSubmit = async () => {
    setGenerating(true);
    setError(null);
    try {
      let sid = sessionId;
      if (!sid) {
        sid = await funnelApi.startSession();
        setSessionId(sid);
      }
      setLoadingMessage('Sending your answers to the coach AI...');
      const startResp = await funnelApi.generate(sid, buildPayload());
      setPlanId(startResp.plan_id);

      // Poll until ready or failed. Big plans (12wk × 7 days = 84 days) can take
      // 3-5 minutes — give up to 8 minutes before showing an error.
      const MESSAGES = [
        'Building your plan...',
        'Tuning exercises to your level...',
        'Writing your nutrition advice...',
        'Final checks and fine-tuning...',
      ];
      let attempts = 0;
      let mi = 0;
      while (true) {
        await new Promise((r) => setTimeout(r, 4000));
        attempts++;
        if (attempts % 3 === 0 && mi < MESSAGES.length - 1) {
          mi++;
          setLoadingMessage(MESSAGES[mi]);
        }
        let polled;
        try {
          polled = await funnelApi.status(startResp.plan_id);
        } catch {
          continue;
        }
        if (polled.status === 'ready') {
          setPlanPreview(polled);
          setGenerating(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        if (polled.status === 'failed') {
          setError(polled.error || 'Generation failed. Please try again.');
          setGenerating(false);
          return;
        }
        if (attempts > 120) {
          setError('This is taking longer than expected. Please try again with a shorter plan duration.');
          setGenerating(false);
          return;
        }
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      let msg = 'Something went wrong. Please try again.';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail) && detail[0]?.msg) {
        msg = detail[0].msg;
      }
      setError(msg);
      setGenerating(false);
    }
  };

  // --- Render ---

  if (planPreview) {
    return <PlanPreview preview={planPreview} />;
  }

  if (generating) {
    return (
      <div className="funnel-shell">
        <div className="funnel-aurora" />
        <div className="funnel-container">
          <div className="funnel-card funnel-loading" data-testid="funnel-loading">
            <div className="funnel-spinner" />
            <h2 className="funnel-loading-title">Building your plan...</h2>
            <p className="funnel-loading-sub">Our coach AI is putting your personal plan together. Longer plans (8-12 weeks) can take up to a few minutes.</p>
            <div className="funnel-loading-step" data-testid="funnel-loading-step">{loadingMessage}</div>
          </div>

          <LoadingExtras />
        </div>
      </div>
    );
  }

  return (
    <div className="funnel-shell">
      <div className="funnel-aurora" />
      <div className="funnel-container">
        <div className="funnel-header">
          <button
            className="funnel-back-btn"
            onClick={handleBack}
            disabled={step === 0}
            data-testid="funnel-back"
            aria-label="Previous step"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="funnel-progress">
            <div className="funnel-progress-bar" style={{ width: `${progressPct}%` }} />
          </div>
          <span className="funnel-step-count" data-testid="funnel-step-count">
            {step + 1} / {totalSteps}
          </span>
        </div>

        <div className="funnel-card">
          <StepRenderer
            stepKey={currentKey}
            answers={answers}
            update={update}
            equipmentList={equipmentList}
          />

          {error && <div className="funnel-error" data-testid="funnel-error">{error}</div>}

          <div className="funnel-actions">
            <button className="funnel-btn-primary" onClick={handleNext} data-testid="funnel-next">
              {step === totalSteps - 1 ? 'Build my plan' : 'Next'}
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Step renderer ---

const StepRenderer = ({ stepKey, answers, update, equipmentList }) => {
  switch (stepKey) {
    case 'training_type':
      return (
        <Step
          title="How do you want to train?"
          hint="Pick what fits you best. This sets the focus of your plan."
          options={TRAINING_TYPES}
          value={answers.training_type}
          onChange={(v) => update({ training_type: v })}
          testId="step-training-type"
          otherValue={answers.training_type_other}
          onOtherChange={(v) => update({ training_type_other: v })}
          otherPlaceholder="e.g. swimming, climbing, martial arts..."
        />
      );
    case 'goal':
      return (
        <Step
          title="What's your main goal?"
          hint="Pick one. The whole plan revolves around this."
          options={GOALS}
          value={answers.goal}
          onChange={(v) => update({ goal: v })}
          testId="step-goal"
          otherValue={answers.goal_other}
          onOtherChange={(v) => update({ goal_other: v })}
          otherPlaceholder="e.g. prep for a Spartan race, post-injury rehab..."
        />
      );
    case 'level':
      return (
        <Step
          title="How experienced are you?"
          hint="Honest answer = better plan. You can level up later."
          options={LEVELS}
          value={answers.level}
          onChange={(v) => update({ level: v })}
          testId="step-level"
        />
      );
    case 'gender_age':
      return <GenderAge answers={answers} update={update} />;
    case 'body_metrics':
      return <BodyMetrics answers={answers} update={update} />;
    case 'pregnancy':
      return <Pregnancy answers={answers} update={update} />;
    case 'days_per_week':
      return <DaysPerWeek answers={answers} update={update} />;
    case 'availability':
      return <Availability answers={answers} update={update} />;
    case 'equipment':
      return <Equipment answers={answers} update={update} equipmentList={equipmentList} />;
    case 'injuries':
      return <Injuries answers={answers} update={update} />;
    case 'diet':
      return <Diet answers={answers} update={update} />;
    case 'plan_duration':
      return (
        <Step
          title="How long should the plan run?"
          hint="Shorter = quicker first wins. Longer = sustainable progression."
          options={PLAN_DURATIONS}
          value={answers.plan_duration_weeks}
          onChange={(v) => update({ plan_duration_weeks: v })}
          testId="step-duration"
        />
      );
    default:
      return null;
  }
};

// --- Reusable Step ---

const Step = ({ title, hint, options, value, onChange, single, testId, otherValue, onOtherChange, otherPlaceholder }) => (
  <div data-testid={testId}>
    <h2 className="funnel-question">{title}</h2>
    {hint && <p className="funnel-hint">{hint}</p>}
    <div className={`funnel-options ${single ? 'single' : ''}`}>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={`funnel-option ${value === opt.value ? 'selected' : ''}`}
          onClick={() => onChange(opt.value)}
          data-testid={`opt-${opt.value}`}
        >
          <span>{opt.label}</span>
          {opt.tag && <span className="funnel-option-tag">{opt.tag}</span>}
        </button>
      ))}
    </div>
    {value === 'other' && onOtherChange && (
      <div className="funnel-other-input" data-testid={`${testId}-other-wrap`}>
        <label className="funnel-label">Tell us in your own words</label>
        <input
          type="text"
          className="funnel-input"
          maxLength={120}
          value={otherValue || ''}
          onChange={(e) => onOtherChange(e.target.value)}
          placeholder={otherPlaceholder || 'Type your answer...'}
          data-testid={`${testId}-other`}
          autoFocus
        />
      </div>
    )}
  </div>
);

// --- Specialised steps ---

const GenderAge = ({ answers, update }) => (
  <div data-testid="step-gender-age">
    <h2 className="funnel-question">A few details about you</h2>
    <p className="funnel-hint">Needed to dial in your calorie and heart-rate zones precisely.</p>
    <div className="funnel-options" style={{ marginBottom: 18 }}>
      {GENDERS.map((g) => (
        <button
          key={g.value}
          type="button"
          className={`funnel-option ${answers.gender === g.value ? 'selected' : ''}`}
          onClick={() => update({ gender: g.value, pregnancy: g.value === 'female' ? answers.pregnancy : { is_pregnant: false } })}
          data-testid={`opt-${g.value}`}
        >
          {g.label}
        </button>
      ))}
    </div>
    {answers.gender === 'other' && (
      <div className="funnel-other-input" data-testid="gender-other-wrap">
        <label className="funnel-label">How do you identify?</label>
        <input
          type="text"
          className="funnel-input"
          maxLength={60}
          value={answers.gender_other || ''}
          onChange={(e) => update({ gender_other: e.target.value })}
          placeholder="Type your answer..."
          data-testid="input-gender-other"
        />
      </div>
    )}
    <div className="funnel-field">
      <label className="funnel-label">Age (years)</label>
      <input
        type="number"
        min="14"
        max="90"
        className="funnel-input"
        value={answers.age ?? ''}
        onChange={(e) => update({ age: e.target.value })}
        placeholder="e.g. 32"
        data-testid="input-age"
      />
    </div>
  </div>
);

const BodyMetrics = ({ answers, update }) => (
  <div data-testid="step-body-metrics">
    <h2 className="funnel-question">Body metrics</h2>
    <p className="funnel-hint">For accurate nutrition math and realistic targets.</p>
    <div className="funnel-row">
      <div className="funnel-field">
        <label className="funnel-label">Height (cm)</label>
        <input
          type="number"
          min="120"
          max="230"
          className="funnel-input"
          value={answers.height_cm ?? ''}
          onChange={(e) => update({ height_cm: e.target.value })}
          placeholder="e.g. 178"
          data-testid="input-height"
        />
      </div>
      <div className="funnel-field">
        <label className="funnel-label">Current weight (kg)</label>
        <input
          type="number"
          step="0.1"
          min="35"
          max="250"
          className="funnel-input"
          value={answers.weight_kg ?? ''}
          onChange={(e) => update({ weight_kg: e.target.value })}
          placeholder="e.g. 78"
          data-testid="input-weight"
        />
      </div>
    </div>
    <div className="funnel-field" style={{ marginTop: 14 }}>
      <label className="funnel-label">Target weight (kg) — optional</label>
      <input
        type="number"
        step="0.1"
        min="35"
        max="250"
        className="funnel-input"
        value={answers.target_weight_kg ?? ''}
        onChange={(e) => update({ target_weight_kg: e.target.value })}
        placeholder="leave empty if you have no specific target"
        data-testid="input-target-weight"
      />
    </div>
  </div>
);

const Pregnancy = ({ answers, update }) => {
  const setPreg = (patch) => update({ pregnancy: { ...answers.pregnancy, ...patch } });
  return (
    <div data-testid="step-pregnancy">
      <h2 className="funnel-question">Are you currently pregnant?</h2>
      <p className="funnel-hint">We adapt the plan safely for pregnancy.</p>
      <div className="funnel-options" style={{ marginBottom: 18 }}>
        <button
          type="button"
          className={`funnel-option ${answers.pregnancy?.is_pregnant === false ? 'selected' : ''}`}
          onClick={() => setPreg({ is_pregnant: false, weeks: null, due_date: null })}
          data-testid="opt-preg-no"
        >No</button>
        <button
          type="button"
          className={`funnel-option ${answers.pregnancy?.is_pregnant === true ? 'selected' : ''}`}
          onClick={() => setPreg({ is_pregnant: true })}
          data-testid="opt-preg-yes"
        >Yes</button>
      </div>
      {answers.pregnancy?.is_pregnant && (
        <div className="funnel-row">
          <div className="funnel-field">
            <label className="funnel-label">How many weeks?</label>
            <input
              type="number"
              min="0"
              max="42"
              className="funnel-input"
              value={answers.pregnancy.weeks ?? ''}
              onChange={(e) => setPreg({ weeks: e.target.value ? Number(e.target.value) : null })}
              placeholder="e.g. 14"
              data-testid="input-preg-weeks"
            />
          </div>
          <div className="funnel-field">
            <label className="funnel-label">Due date</label>
            <input
              type="date"
              className="funnel-input"
              value={answers.pregnancy.due_date ?? ''}
              onChange={(e) => setPreg({ due_date: e.target.value })}
              data-testid="input-preg-due"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const DaysPerWeek = ({ answers, update }) => (
  <div data-testid="step-days-per-week">
    <h2 className="funnel-question">How many days per week can you train?</h2>
    <p className="funnel-hint">Be realistic — sticking to the plan beats over-promising.</p>
    <div className="funnel-chip-group">
      {[1, 2, 3, 4, 5, 6, 7].map((n) => (
        <button
          key={n}
          type="button"
          className={`funnel-chip ${answers.days_per_week === n ? 'selected' : ''}`}
          onClick={() => update({ days_per_week: n })}
          data-testid={`opt-days-${n}`}
        >
          {n} {n === 1 ? 'day' : 'days'}
        </button>
      ))}
    </div>
  </div>
);

const Availability = ({ answers, update }) => {
  const setDay = (idx, patch) => {
    const next = [...answers.availability];
    next[idx] = { ...next[idx], ...patch };
    update({ availability: next });
  };
  return (
    <div data-testid="step-availability">
      <h2 className="funnel-question">When can you train?</h2>
      <p className="funnel-hint">Tick the days and pick a time slot. We'll plan your week around this.</p>
      <div className="funnel-availability">
        {answers.availability.map((d, i) => (
          <div className="funnel-availability-row" key={d.day}>
            <span className="funnel-availability-day">{DAY_LABELS[d.day]}</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#c4c7d5', fontSize: 13 }}>
              <input
                type="checkbox"
                checked={d.available}
                onChange={(e) => setDay(i, { available: e.target.checked })}
                data-testid={`avail-${d.day}`}
              />
              Available
            </label>
            <select
              value={d.time_slot || 'evening'}
              disabled={!d.available}
              onChange={(e) => setDay(i, { time_slot: e.target.value })}
              data-testid={`slot-${d.day}`}
            >
              <option value="morning">Morning</option>
              <option value="afternoon">Afternoon</option>
              <option value="evening">Evening</option>
            </select>
            <input
              type="number"
              className="min-input"
              min="15"
              max="240"
              step="5"
              value={d.minutes || 60}
              disabled={!d.available}
              onChange={(e) => setDay(i, { minutes: Number(e.target.value) })}
              placeholder="min"
              data-testid={`min-${d.day}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const Equipment = ({ answers, update, equipmentList }) => {
  const toggle = (item) => {
    const has = answers.equipment.includes(item);
    let next;
    if (item === 'none') {
      next = has ? [] : ['none'];
    } else {
      next = answers.equipment.filter((e) => e !== 'none');
      if (has) next = next.filter((e) => e !== item);
      else next = [...next, item];
    }
    update({ equipment: next });
  };
  return (
    <div data-testid="step-equipment">
      <h2 className="funnel-question">What equipment do you have?</h2>
      <p className="funnel-hint">Pick everything you can use. We'll build the plan around what you actually have.</p>
      <div className="funnel-chip-group">
        {equipmentList.map((item) => (
          <button
            key={item}
            type="button"
            className={`funnel-chip ${answers.equipment.includes(item) ? 'selected' : ''}`}
            onClick={() => toggle(item)}
            data-testid={`eq-${item}`}
          >
            {answers.equipment.includes(item) ? <Check size={14} /> : null}
            {EQUIPMENT_LABELS[item] || item}
          </button>
        ))}
      </div>
    </div>
  );
};

const Injuries = ({ answers, update }) => (
  <div data-testid="step-injuries">
    <h2 className="funnel-question">Any injuries or limitations?</h2>
    <p className="funnel-hint">Optional. The more specific, the safer the plan. Leave empty if you have none.</p>
    <textarea
      className="funnel-textarea"
      maxLength={600}
      value={answers.injuries || ''}
      onChange={(e) => update({ injuries: e.target.value })}
      placeholder="e.g. mild lower back pain, old left knee injury..."
      data-testid="input-injuries"
    />
  </div>
);

const Diet = ({ answers, update }) => {
  const [allergyInput, setAllergyInput] = useState('');
  const [dislikeInput, setDislikeInput] = useState('');

  const addItem = (key, val, setter) => {
    const v = val.trim();
    if (!v) return;
    if (answers[key].includes(v)) { setter(''); return; }
    if (answers[key].length >= 20) return;
    update({ [key]: [...answers[key], v] });
    setter('');
  };

  const removeItem = (key, val) => {
    update({ [key]: answers[key].filter((x) => x !== val) });
  };

  return (
    <div data-testid="step-diet">
      <h2 className="funnel-question">Diet & preferences</h2>
      <p className="funnel-hint">We'll tune your nutrition plan to what you do and don't eat.</p>
      <div className="funnel-options" style={{ marginBottom: 18 }}>
        {DIET_TYPES.map((d) => (
          <button
            key={d.value}
            type="button"
            className={`funnel-option ${answers.diet_type === d.value ? 'selected' : ''}`}
            onClick={() => update({ diet_type: d.value })}
            data-testid={`opt-diet-${d.value}`}
          >
            {d.label}
          </button>
        ))}
      </div>
      {answers.diet_type === 'other' && (
        <div className="funnel-other-input" data-testid="diet-other-wrap">
          <label className="funnel-label">Describe your diet</label>
          <input
            type="text"
            className="funnel-input"
            maxLength={120}
            value={answers.diet_type_other || ''}
            onChange={(e) => update({ diet_type_other: e.target.value })}
            placeholder="e.g. carnivore, FODMAP-restricted, intermittent fasting..."
            data-testid="input-diet-other"
          />
        </div>
      )}

      <div className="funnel-field" style={{ marginBottom: 16 }}>
        <label className="funnel-label">Allergies</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            className="funnel-input"
            value={allergyInput}
            onChange={(e) => setAllergyInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem('allergies', allergyInput, setAllergyInput); } }}
            placeholder="e.g. peanut, lactose..."
            data-testid="input-allergy"
          />
          <button type="button" className="funnel-btn-ghost" onClick={() => addItem('allergies', allergyInput, setAllergyInput)} data-testid="add-allergy">Add</button>
        </div>
        <div className="funnel-chip-group" style={{ marginTop: 8 }}>
          {answers.allergies.map((a) => (
            <span key={a} className="funnel-chip selected" onClick={() => removeItem('allergies', a)}>
              {a} <X size={12} />
            </span>
          ))}
        </div>
      </div>

      <div className="funnel-field">
        <label className="funnel-label">Foods you dislike / want to avoid</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            className="funnel-input"
            value={dislikeInput}
            onChange={(e) => setDislikeInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem('dislikes', dislikeInput, setDislikeInput); } }}
            placeholder="e.g. broccoli, fish..."
            data-testid="input-dislike"
          />
          <button type="button" className="funnel-btn-ghost" onClick={() => addItem('dislikes', dislikeInput, setDislikeInput)} data-testid="add-dislike">Add</button>
        </div>
        <div className="funnel-chip-group" style={{ marginTop: 8 }}>
          {answers.dislikes.map((a) => (
            <span key={a} className="funnel-chip selected" onClick={() => removeItem('dislikes', a)}>
              {a} <X size={12} />
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

// --- Plan preview (locked) ---

const PlanPreview = ({ preview }) => {
  return (
    <div className="funnel-shell" data-testid="plan-preview">
      <div className="funnel-aurora" />
      <div className="funnel-container" style={{ maxWidth: 820 }}>
        <div className="plan-overview">
          <h2><Sparkles size={20} style={{ verticalAlign: -3, marginRight: 6, color: '#a78bfa' }} /> Your personal plan</h2>
          <p>{preview.overview}</p>
        </div>

        <div className="plan-overview">
          <h2 style={{ fontSize: 17 }}>Weekly structure</h2>
          <p>{preview.weekly_structure}</p>
        </div>

        {preview.unlocked_days.map((day) => (
          <div className="plan-day" key={day.day_number} data-testid={`plan-day-${day.day_number}`}>
            <div className="plan-day-header">
              <span className="plan-day-number">Day {day.day_number}</span>
              <h3 className="plan-day-title">{day.title}</h3>
            </div>
            <p className="plan-day-focus">{day.focus} · {day.estimated_duration_min} min</p>

            {day.warmup?.length > 0 && (
              <div className="plan-section">
                <p className="plan-section-label">Warm-up</p>
                <ul className="plan-list">{day.warmup.map((w, i) => <li key={i}>{w}</li>)}</ul>
              </div>
            )}

            <div className="plan-section">
              <p className="plan-section-label">Exercises</p>
              <div className="plan-ex-table">
                {day.exercises.map((ex, i) => (
                  <div className="plan-ex-row" key={i}>
                    <div>
                      <div className="plan-ex-name">{ex.name}</div>
                      {ex.notes && <div className="plan-ex-notes">{ex.notes}</div>}
                    </div>
                    <div className="plan-ex-meta">
                      {ex.sets ? `${ex.sets}×${ex.reps || ''}` : (ex.duration_min ? `${ex.duration_min} min` : '')}
                      {ex.rest_sec ? ` · rest ${ex.rest_sec}s` : ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {day.cooldown?.length > 0 && (
              <div className="plan-section">
                <p className="plan-section-label">Cool-down</p>
                <ul className="plan-list">{day.cooldown.map((c, i) => <li key={i}>{c}</li>)}</ul>
              </div>
            )}

            {day.nutrition_tip && (
              <div className="plan-nutrition">{day.nutrition_tip}</div>
            )}
          </div>
        ))}

        {/* Locked teaser — directs to the app stores, no website signup */}
        {preview.locked_days_count > 0 && (
          <div className="plan-locked" data-testid="plan-locked">
            <div className="plan-locked-icon"><Lock size={26} /></div>
            <h3>Unlock your full plan in the AthloPeak app</h3>
            <p>You've seen a preview of the first 2 days. Download the app to unlock all {preview.total_days} workouts plus your full nutrition plan. Your free 14-day trial starts the moment you create your account in the app.</p>
            <div className="plan-locked-stats">
              <div className="plan-locked-stat"><div className="v">{preview.total_days}</div><div className="l">workouts</div></div>
              <div className="plan-locked-stat"><div className="v">{preview.locked_days_count}</div><div className="l">locked</div></div>
              <div className="plan-locked-stat"><div className="v">14</div><div className="l">days free</div></div>
            </div>

            <div className="plan-locked-stores" data-testid="plan-locked-stores">
              <a
                href="https://apps.apple.com/nl/app/athlopeak/id6751170384"
                target="_blank"
                rel="noopener noreferrer"
                className="store-button app-store"
                data-testid="locked-app-store"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="store-icon">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div className="store-button-text">
                  <span className="store-button-label">Download on the</span>
                  <span className="store-button-name">App Store</span>
                </div>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.athlopeak"
                target="_blank"
                rel="noopener noreferrer"
                className="store-button google-play"
                data-testid="locked-google-play"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="store-icon">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                <div className="store-button-text">
                  <span className="store-button-label">GET IT ON</span>
                  <span className="store-button-name">Google Play</span>
                </div>
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// --- Loading screen extras: roterende weetjes + social follow ---

const LoadingExtras = () => {
  const [factIdx, setFactIdx] = useState(() => Math.floor(Math.random() * FITNESS_FACTS.length));

  useEffect(() => {
    const id = setInterval(() => {
      setFactIdx((prev) => (prev + 1) % FITNESS_FACTS.length);
    }, 5000);
    return () => clearInterval(id);
  }, []);

  const fact = FITNESS_FACTS[factIdx];

  return (
    <div data-testid="loading-extras">
      <div className="loading-fact" key={factIdx}>
        <span className="loading-fact-cat">{fact.c}</span>
        <p className="loading-fact-text">{fact.t}</p>
      </div>

      <div className="loading-social">
        <p className="loading-social-title">Follow along while you wait</p>
        <div className="loading-social-row">
          <a
            href="https://instagram.com/athlopeak"
            target="_blank"
            rel="noopener noreferrer"
            className="loading-social-btn"
            data-testid="loading-instagram"
          >
            <Instagram size={18} />
            <div>
              <span className="loading-social-platform">Instagram</span>
              <span className="loading-social-handle">@athlopeak</span>
            </div>
          </a>
          <a
            href="https://tiktok.com/@athlopeak"
            target="_blank"
            rel="noopener noreferrer"
            className="loading-social-btn"
            data-testid="loading-tiktok"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12.525 2c1.31 0 2.625.013 3.94.013 0 1.575.563 3.144 1.682 4.252 1.108 1.108 2.692 1.671 4.252 1.671v3.94c-1.471 0-2.943-.337-4.252-.998-.563-.272-1.107-.616-1.638-1.029v8.04c0 4.252-3.453 7.694-7.694 7.694S1.121 22.142 1.121 17.89c0-4.252 3.452-7.694 7.694-7.694.563 0 1.107.063 1.638.183v4.054c-.531-.183-1.075-.272-1.638-.272-2.077 0-3.754 1.671-3.754 3.754s1.677 3.754 3.754 3.754c2.077 0 3.754-1.671 3.754-3.754V2z"/>
            </svg>
            <div>
              <span className="loading-social-platform">TikTok</span>
              <span className="loading-social-handle">@athlopeak</span>
            </div>
          </a>
        </div>
      </div>
    </div>
  );
};

export default Funnel;
